import { memo, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { useProgramHistoryPageRedirect } from '@pages/programs/history/index.ts';
import { useProgramPageRedirect } from '@pages/programs/program-page/helpres.ts';
import { Header } from '@widgets/header';
import { Search, useFilter } from '@features/common';
import { ProgramsListBody } from '@features/programs/listBody/index.tsx';
import {
  AddThin,
  ArrowShortLeft,
  FilterIcon,
  GridViewIcon,
  LibraryIcon,
  ListViewIcon,
  Search as SearchIcon,
  VideoProgramsHistoryIcon,
} from '@shared/assets';
import { useMobile, usePlatformSettings, useUser } from '@shared/hooks';
import { useListTabs } from '@shared/hooks/use-list-tabs.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import { useProgramsListViewType } from '@shared/hooks/use-programs-list-view-type.ts';
import { useSaveScroll } from '@shared/hooks/use-save-scroll.ts';
import type { ActiveTabType } from '@shared/types/common';
import { IconButton, NavTab, PageHead } from '@shared/ui';
import { FilterMobileActions } from '@shared/ui/filter-mobile-actions/index.ts';
import * as st from './styles.module.scss';

export const ProgramList: React.FC = memo(() => {
  const { redirectToHistoryPage } = useProgramHistoryPageRedirect();
  const { setListScrollData, clearListScrollData } = useSaveScroll('VideoProgramsList');

  const {
    selectedTags,
    onTagChange,
    search,
    onSearch,
    selectedAuthors,
    onAuthorChange,
    isSearchMobile,
    setIsSearchMobile,
    isFilterActive,
    onPartitionsChange,
    selectedPartitions,
    partitions,
    authors,
    tags,
    handleResetFilter,
  } = useFilter();

  const [titles, setTitles] = useState<string[]>([]);
  const { module, permissions } = useModuleByCodeParam();
  const [mobileWithFilterOn, setMobileWithFilterOn] = useState(false);
  const { isMobile } = useMobile();

  const [showFilterBar, setShowFilterBar] = useState(() => {
    if (isMobile) {
      return false;
    }
    return isFilterActive;
  });

  const { tabs, onTabClick, activeTab } = useListTabs();

  const { setProgramsListViewType, isListView } = useProgramsListViewType({ activeTab: activeTab });

  const { doSavePageUrl: savePageUrlForProgramListPage } = usePagesRedirect('programsList');
  const { doRedirectWithSavePageUrl: redirectOnProgramEditorPage } =
    usePagesRedirect('programEditor');
  const { doSavePageUrl: saveProgramsListPageUrl } = useProgramPageRedirect();
  const { isAuth } = useUser();

  /////////////////////////////////////////////////////////////
  const { has_library } = usePlatformSettings();

  const filterParams = {
    partitions: selectedPartitions.length ? selectedPartitions.join(',') : undefined,
    tags: selectedTags.length ? selectedTags.join(',') : undefined,
    authors: selectedAuthors.length ? selectedAuthors.join(',') : undefined,
    search: search.length ? search : undefined,
  };

  const IsActiveFilterBtn = useMemo(
    () => Boolean(showFilterBar || isFilterActive),
    [showFilterBar, isFilterActive]
  );

  const handleShowFilterBar = (): void => {
    setShowFilterBar((prev) => !prev);
  };

  const handleAddProgram = useCallback(() => {
    savePageUrlForProgramListPage();
    redirectOnProgramEditorPage(`program-edit/new`);
    saveProgramsListPageUrl();
  }, [savePageUrlForProgramListPage, redirectOnProgramEditorPage]);

  const LibraryAndAddProgramNodes = useMemo(() => {
    if (permissions.can_sell && activeTab === 'my') {
      return (
        <>
          {has_library && (
            <Link to={`exercises`} onClick={savePageUrlForProgramListPage}>
              <Tooltip title="Библиотека">
                <IconButton variant="dark">
                  <LibraryIcon />
                </IconButton>
              </Tooltip>
            </Link>
          )}
          <Tooltip title="Добавить программу">
            <IconButton variant="dark" onClick={handleAddProgram}>
              <AddThin />
            </IconButton>
          </Tooltip>
        </>
      );
    }
  }, [permissions, activeTab, has_library, handleAddProgram, savePageUrlForProgramListPage]);
  return (
    <>
      {!isSearchMobile && (
        <>
          <Header
            MCenter={<>{mobileWithFilterOn ? 'Фильтр' : module?.verbose_name}</>}
            menuClassName={`${mobileWithFilterOn ? st.hide : ''}`}
          />
          <PageHead
            Left={
              <>
                {!mobileWithFilterOn ? (
                  Object.keys(tabs).map((key) => (
                    <NavTab
                      isActive={key === activeTab}
                      key={key}
                      onClick={() => {
                        clearListScrollData();
                        onTabClick(key as ActiveTabType);
                      }}
                      onTouchStart={(e) => e.preventDefault()}
                    >
                      {tabs[key]}
                    </NavTab>
                  ))
                ) : (
                  <></>
                )}
              </>
            }
            Right={
              <>
                {mobileWithFilterOn ? (
                  <FilterMobileActions
                    isFilterActive={isFilterActive}
                    onReset={handleResetFilter}
                    onConfirm={() => {
                      setMobileWithFilterOn(false);
                      setShowFilterBar((prev) => !prev);
                    }}
                  />
                ) : (
                  <>
                    {isMobile ? (
                      <IconButton
                        variant="mobile"
                        onClick={() => setIsSearchMobile(!isSearchMobile)}
                      >
                        <SearchIcon />
                      </IconButton>
                    ) : (
                      <Search
                        isOpenInit={search !== ''}
                        search={search}
                        onSearch={onSearch}
                        titles={titles}
                      />
                    )}
                    {activeTab === 'all' && !isMobile && (
                      <IconButton
                        variant={'dark'}
                        onClick={() => {
                          setProgramsListViewType(isListView ? 'grid' : 'list');
                        }}
                      >
                        {isListView ? <GridViewIcon /> : <ListViewIcon />}
                      </IconButton>
                    )}

                    {activeTab === 'all' && isAuth && (
                      <Tooltip title="История">
                        <IconButton variant={'dark'} onClick={redirectToHistoryPage}>
                          <VideoProgramsHistoryIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Фильтр">
                      <IconButton
                        variant={`${IsActiveFilterBtn ? 'activeDark' : 'dark'}`}
                        onClick={() => {
                          showFilterBar && handleResetFilter();
                          isMobile && setMobileWithFilterOn(true);
                          handleShowFilterBar();
                        }}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Tooltip>

                    {LibraryAndAddProgramNodes}
                  </>
                )}
              </>
            }
          />
        </>
      )}

      {isSearchMobile && (
        <>
          <Header MCenter={<>Поиск</>} isHiddenHamburger={isSearchMobile} />
          <PageHead
            Left={
              <IconButton
                variant="dark"
                onClick={() => {
                  setIsSearchMobile(!isSearchMobile);
                  onSearch('');
                }}
              >
                <ArrowShortLeft />
              </IconButton>
            }
            Right={
              <>
                <Search
                  mobile
                  isOpenInit={search !== ''}
                  search={search}
                  onSearch={onSearch}
                  titles={titles}
                />
              </>
            }
          />
        </>
      )}

      <ProgramsListBody
        filterData={{
          tags,
          partitions,
          authors,
          activeTab: activeTab,
          handleResetFilter: handleResetFilter,
          isOpen: showFilterBar,
          selectedTags: selectedTags,
          hasResetButton: !mobileWithFilterOn && isFilterActive,
          onTagChange: onTagChange,
          selectedPartitions: selectedPartitions,
          onPartitionChange: (id) => onPartitionsChange([id]),
          selectedAuthors: selectedAuthors,
          onAuthorChange: onAuthorChange,
        }}
        params={filterParams}
        setTitles={setTitles}
        tabName={activeTab}
        doSetStorageData={setListScrollData}
      />
    </>
  );
});
